import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import AdminSubNav from "../components/AdminSubNav";
import { useAdminAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { withImageVersion } from "../lib/imageVersion";
import { ShieldCheck, AlertTriangle, ImageOff, Radio, Download } from "lucide-react";

const EVENT_LABELS = {
    bridge_ready: "Bridge ready",
    angel_selected: "Angel selected",
    selection_cleared: "Selection cleared",
    download_url: "Download",
    new_design: "New design",
};

function bridgeEventDetail(e) {
    const p = e.payload || {};
    if (p.angelNumber) return `Angel ${p.angelNumber}${p.label ? ` · ${p.label}` : ""}`;
    if (p.currentAngel) return `Angel ${p.currentAngel}`;
    if (p.filename) return `${p.filename}${p.method ? ` · ${p.method}` : ""}`;
    return "—";
}

function BridgeEventsLog() {
    const [data, setData] = useState(null);
    const [typeFilter, setTypeFilter] = useState("all");
    const [memberFilter, setMemberFilter] = useState("");

    useEffect(() => {
        let cancelled = false;
        api.get("/admin/bridge/events")
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch(() => { if (!cancelled) setData({ events: [], last24h: {}, total: 0 }); });
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => {
        if (!data) return [];
        const q = memberFilter.trim().toLowerCase();
        return data.events.filter((e) => {
            if (typeFilter !== "all" && e.type !== typeFilter) return false;
            if (q && !(e.memberId || "").toLowerCase().includes(q)) return false;
            return true;
        });
    }, [data, typeFilter, memberFilter]);

    const exportCsv = async () => {
        try {
            const res = await api.get("/admin/bridge/events/export", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `bridge-events-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 4000);
        } catch { /* silent — button stays usable */ }
    };

    return (
        <div className="mt-16" data-testid="bridge-events-section">
            <div className="flex items-center gap-3 mb-2">
                <Radio className="w-4 h-4 text-[#cfa052]" aria-hidden="true" />
                <h2 className="font-serif-display text-lg text-[#e8e4db] tracking-tight">Native bridge events</h2>
                {data && (
                    (() => {
                        const lastTs = data.events?.[0]?.timestamp;
                        const live = lastTs && Date.now() - new Date(lastTs).getTime() < 3600 * 1000;
                        return live ? (
                            <span className="ml-2 inline-flex items-center gap-1.5 font-label text-[9px] tracking-[0.2em] uppercase text-emerald-300 border border-emerald-400/30 bg-emerald-500/10 rounded-full px-2.5 py-0.5" data-testid="bridge-live-badge">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
                                Shell heard us in the last hour
                            </span>
                        ) : (
                            <span className="ml-2 inline-flex items-center gap-1.5 font-label text-[9px] tracking-[0.2em] uppercase text-[#6e6a62] border border-white/10 rounded-full px-2.5 py-0.5" data-testid="bridge-quiet-badge">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#6e6a62]" aria-hidden="true" />
                                Quiet — no events in the last hour
                            </span>
                        );
                    })()
                )}
            </div>
            <p className="font-poetic text-sm text-[#9a958a] mb-6 max-w-2xl">
                Every event the portal fires at the native shell is mirrored here — if these are flowing, the Inspirations app is hearing us.
            </p>
            {!data ? (
                <p className="font-poetic text-[#6e6a62]">Listening…</p>
            ) : (
                <>
                    <div className="flex flex-wrap gap-3 mb-6" data-testid="bridge-24h-counts">
                        {Object.keys(EVENT_LABELS).map((t) => (
                            <div key={t} className="border border-white/10 bg-white/[0.02] rounded px-4 py-2">
                                <span className="font-numerals text-lg text-[#cfa052]">{data.last24h?.[t] || 0}</span>
                                <span className="ml-2 font-label text-[8px] tracking-[0.2em] uppercase text-[#6e6a62]">{EVENT_LABELS[t]} · 24h</span>
                            </div>
                        ))}
                    </div>
                    {!data.events.length ? (
                        <div className="border border-white/10 bg-white/[0.02] rounded p-8 text-center" data-testid="bridge-events-empty">
                            <p className="font-poetic text-[#6e6a62]">No bridge events recorded yet — they'll appear as members open the portal in the shell.</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="bridge-filters">
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    className="bg-[#0c0b0a] border border-white/10 rounded px-3 py-2 font-label text-[10px] tracking-[0.15em] uppercase text-[#9a958a] focus:border-[#cfa052]/60 focus:outline-none"
                                    data-testid="bridge-filter-type"
                                >
                                    <option value="all">All events</option>
                                    {Object.entries(EVENT_LABELS).map(([t, label]) => (
                                        <option key={t} value={t}>{label}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    value={memberFilter}
                                    onChange={(e) => setMemberFilter(e.target.value)}
                                    placeholder="Filter by member id…"
                                    className="flex-1 max-w-xs bg-[#0c0b0a] border border-white/10 rounded px-3 py-2 font-poetic text-sm text-[#e8e4db] placeholder:text-[#6e6a62] focus:border-[#cfa052]/60 focus:outline-none"
                                    data-testid="bridge-filter-member"
                                />
                                <span className="font-label text-[9px] tracking-[0.2em] uppercase text-[#6e6a62]" data-testid="bridge-filter-count">
                                    {filtered.length} of {data.events.length}
                                </span>
                                <button
                                    type="button"
                                    onClick={exportCsv}
                                    className="ml-auto inline-flex items-center gap-1.5 border border-white/10 text-[#cfa052] hover:text-[#e0b569] font-label text-[9px] tracking-[0.2em] uppercase px-3 py-2 rounded transition-colors"
                                    data-testid="bridge-export-btn"
                                >
                                    <Download className="w-3 h-3" /> Export CSV
                                </button>
                            </div>
                            {!filtered.length ? (
                                <div className="border border-white/10 bg-white/[0.02] rounded p-8 text-center" data-testid="bridge-filter-no-match">
                                    <p className="font-poetic text-[#6e6a62]">No events match this filter.</p>
                                </div>
                            ) : (
                                <div className="border border-white/10 rounded overflow-hidden" data-testid="bridge-events-table">
                                    {filtered.slice(0, 25).map((e) => (
                                        <div key={e.id} className="flex items-center gap-4 px-4 py-2.5 border-b border-white/5 last:border-b-0 bg-white/[0.01]" data-testid={`bridge-event-${e.id}`}>
                                            <span className="font-label text-[9px] tracking-[0.15em] uppercase text-[#cfa052] w-32 shrink-0">{EVENT_LABELS[e.type] || e.type}</span>
                                            <span className="font-poetic text-xs text-[#9a958a] flex-1 truncate">{bridgeEventDetail(e)}</span>
                                            <span className="font-poetic text-xs text-[#6e6a62] truncate max-w-[180px]">{e.memberId || "anonymous"}</span>
                                            <span className="font-label text-[9px] text-[#6e6a62] shrink-0">{new Date(e.timestamp).toLocaleString()}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function Thumb({ img, number }) {
    const [broken, setBroken] = useState(false);
    const missing = img.ok === false;
    return (
        <figure className="w-20" data-testid={`health-thumb-${number}-${img.kind}-${img.label.replace(/\s+/g, "-").toLowerCase()}`}>
            {missing || broken ? (
                <div className="w-20 h-24 flex flex-col items-center justify-center gap-1 bg-[#2a1512] border border-[#c96f4a]/60 rounded-sm text-[#c96f4a]">
                    <ImageOff className="w-4 h-4" aria-hidden="true" />
                    <span className="font-label text-[7px] tracking-[0.15em] uppercase">{missing ? "missing" : "broken"}</span>
                </div>
            ) : (
                <img
                    src={withImageVersion(img.url, img.version)}
                    alt={`Angel ${number} ${img.label}`}
                    onError={() => setBroken(true)}
                    className="w-20 h-24 object-cover bg-[#0c0b0a] border border-white/10 rounded-sm"
                />
            )}
            <figcaption className={`mt-1 font-label text-[8px] tracking-[0.12em] uppercase leading-tight ${missing || broken ? "text-[#c96f4a]" : "text-[#6e6a62]"}`}>
                {img.label}
            </figcaption>
        </figure>
    );
}

export default function AdminArtworkHealth() {
    const { admin } = useAdminAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [issuesOnly, setIssuesOnly] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get("/admin/artwork-health");
                if (!cancelled) {
                    setData(res.data);
                    setIssuesOnly(res.data.withIssues > 0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const shown = useMemo(() => {
        if (!data) return [];
        const list = issuesOnly ? data.angels.filter((a) => a.hasIssue) : data.angels;
        return [...list].sort((a, b) => (b.hasIssue - a.hasIssue) || a.number.localeCompare(b.number));
    }, [data, issuesOnly]);

    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;

    return (
        <PortalLayout>
            <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32" data-testid="admin-health-page">
                <div className="mb-10">
                    <span className="font-label text-[11px] text-[#cfa052]">Admin · artwork health</span>
                    <h1 className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]">
                        Health <span className="italic">check</span>
                    </h1>
                    <p className="mt-4 font-poetic text-[#9a958a] max-w-2xl">
                        Every sketch and design at a glance. Files with missing bytes are flagged automatically — and the thumbnails let you eyeball anything that looks corrupted.
                    </p>
                </div>

                <AdminSubNav />

                {loading ? (
                    <p className="font-poetic text-[#6e6a62]">Inspecting the gallery…</p>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                            <div className={`border rounded p-5 ${data.withIssues ? "border-[#c96f4a]/50 bg-[#c96f4a]/[0.06]" : "border-white/10 bg-white/[0.02]"}`} data-testid="health-issues-pill">
                                <div className={`font-numerals text-3xl ${data.withIssues ? "text-[#c96f4a]" : "text-[#cfa052]"}`}>{data.withIssues}</div>
                                <div className="mt-1 font-label text-[9px] tracking-[0.25em] uppercase text-[#6e6a62]">Angels with issues</div>
                            </div>
                            <div className="border border-white/10 bg-white/[0.02] rounded p-5" data-testid="health-healthy-pill">
                                <div className="font-numerals text-3xl text-[#cfa052]">{data.total - data.withIssues}</div>
                                <div className="mt-1 font-label text-[9px] tracking-[0.25em] uppercase text-[#6e6a62]">Healthy angels</div>
                            </div>
                            <div className="border border-white/10 bg-white/[0.02] rounded p-5" data-testid="health-images-pill">
                                <div className="font-numerals text-3xl text-[#cfa052]">{data.imagesChecked}</div>
                                <div className="mt-1 font-label text-[9px] tracking-[0.25em] uppercase text-[#6e6a62]">Images checked</div>
                            </div>
                        </div>

                        <label className="inline-flex items-center gap-2 mb-8 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={issuesOnly}
                                onChange={(e) => setIssuesOnly(e.target.checked)}
                                data-testid="health-issues-only-toggle"
                                className="accent-[#cfa052]"
                            />
                            <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a]">Issues only</span>
                        </label>

                        {!shown.length ? (
                            <div className="border border-white/10 bg-white/[0.02] rounded p-10 text-center" data-testid="health-all-clear">
                                <ShieldCheck className="w-6 h-6 text-[#cfa052] mx-auto" aria-hidden="true" />
                                <p className="mt-4 font-poetic text-[#9a958a]">All artwork accounted for — nothing missing.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {shown.map((a) => (
                                    <div
                                        key={a.number}
                                        className={`border rounded p-5 ${a.hasIssue ? "border-[#c96f4a]/50 bg-[#c96f4a]/[0.04]" : "border-white/10 bg-white/[0.02]"}`}
                                        data-testid={`health-angel-${a.number}`}
                                    >
                                        <div className="flex items-center gap-3 mb-4">
                                            {a.hasIssue
                                                ? <AlertTriangle className="w-4 h-4 text-[#c96f4a]" aria-hidden="true" />
                                                : <ShieldCheck className="w-4 h-4 text-[#cfa052]" aria-hidden="true" />}
                                            <span className="font-numerals text-lg text-[#e8e4db]">№ {a.number}</span>
                                            {!a.hasArtwork && (
                                                <span className="font-label text-[9px] tracking-[0.2em] uppercase text-[#6e6a62]">no artwork yet</span>
                                            )}
                                        </div>
                                        {a.images.length ? (
                                            <div className="flex flex-wrap gap-3">
                                                {a.images.map((img, i) => <Thumb key={`${img.url}-${i}`} img={img} number={a.number} />)}
                                            </div>
                                        ) : (
                                            <p className="font-poetic text-sm text-[#6e6a62]">
                                                {a.hasArtwork ? "Artwork flagged but no image records found." : "Awaiting first upload."}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                <BridgeEventsLog />
            </section>
        </PortalLayout>
    );
}
